import { ActionSettings, BasicSelect, ICONS, Modal, ToolbarButton } from '@fl/cmsch-fe-library';
import React, { FC, Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Ant } from 'common/ant';
import { ConnectedTable } from 'utils/tables';
import { managementAssociationsColumns } from './columns';
import { useManagementAssociationsSetup } from '../../hooks/use-management-associations-setup';
import { adminAction } from '../../model/action';
import { simpleAdminSelector } from '../../model/selector';
import { ManagementAssociationsTableType } from '../../types/management-associations';
import { SubjectManagementForm } from '../subject-management-form';

// eslint-disable-next-line max-lines-per-function
const ManagementAssociationsTableBase: FC = _ => {
  const dispatch = useDispatch();
  const {
    associationId,
    associationsOptions,
    managementAssociationsExtraColumns,
    roleOptions,
    userProfileAndRoles,
    userProfileOptions,
    visibleModal,
    modalTitle,
    associationDetail,
    isEdit,
    handleCloseModal,
    handleColumnOpenCreateModal,
    handleOpenCreateModalWithoutLimitations,
    handleRoleSearch,
    handleRowOpenCreateModal,
    handleSubmitModal,
    handleUserSearch,
    validate,
    validateDebounced,
    t,
    tCommon,
    searchManagementAssociations,
    onManagementAssociationsTableFetch,
    handleManagementAssociationSelectChangeValue,
    removeConfirmTitle,
    removeRolesForUser,
    removeUsersFromRole,
    rowMapper,
    clearAssociationDetail
  } = useManagementAssociationsSetup();
  const leftHeaderCustomElement = useMemo(() => <BasicSelect value={associationId} options={associationsOptions} onFieldChange={handleManagementAssociationSelectChangeValue} onSearch={searchManagementAssociations} ignoreSearchMinLength fullWidth testId="association-select" />, [associationId, associationsOptions, handleManagementAssociationSelectChangeValue, searchManagementAssociations]);
  useEffect(() => {
    dispatch(adminAction.getManagementAssociationsOptions(''));
  }, [dispatch]);
  const addAccessToUserDisabledTitle = useCallback((_id: number, user: ManagementAssociationsTableType) => t('cannotCreateRecord', {
    user: user.username
  }), [t]);
  const isAddAccessToUserDisabled = useCallback((_id: number, user: ManagementAssociationsTableType) => !user.isSubjectMember, []);
  const actionSettings: ActionSettings<ManagementAssociationsTableType> = useMemo(() => ({
    confirmRowRemove: true,
    extraActions: [{
      id: 'delete',
      role: 'delete',
      callback: removeRolesForUser,
      confirmClick: true,
      confirmTitle: removeConfirmTitle
    }, {
      id: 'create',
      title: t('addAccessToUser'),
      disabledTitle: addAccessToUserDisabledTitle,
      disabled: isAddAccessToUserDisabled,
      role: 'create',
      callback: handleRowOpenCreateModal
    }]
  }), [removeRolesForUser, removeConfirmTitle, addAccessToUserDisabledTitle, isAddAccessToUserDisabled, t, handleRowOpenCreateModal]);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    title: t('access'),
    role: 'create',
    disabled: associationId.isEmpty,
    disabledTitle: t('needSelectAssociation'),
    onClick: handleOpenCreateModalWithoutLimitations
  }], [associationId, t, handleOpenCreateModalWithoutLimitations]);
  const computedColumns = useMemo(() => associationId.isFull ? managementAssociationsColumns(managementAssociationsExtraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal) : [], [associationId, managementAssociationsExtraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal]);
  return <Fragment>
            <ConnectedTable tableId="managementAssociations" columns={computedColumns} headerButtons={headerButtons} onFetchTableData={onManagementAssociationsTableFetch} rowDataToTableRowMapper={rowMapper} rowDataPageSelector={simpleAdminSelector.managementAssociationsPage} withoutVerticalScrollbar actionSettings={actionSettings} leftHeaderCustomElement={leftHeaderCustomElement} paginationPosition="none" emptyTableMessages={{
      noColumnsVisible: t('needSelectAssociation')
    }} emptyTableBodyContent={ICONS.chooseOption} lazyLoadingLimit={20} showAlsoIfEmpty data-sentry-element="ConnectedTable" data-sentry-source-file="index.tsx" />
            {associationId.isFull && <Modal title={modalTitle} visible={visibleModal} onCancel={handleCloseModal} footer={null}>
                <Ant.Row>
                    <Ant.Col xs={24}>
                        <SubjectManagementForm isEdit={isEdit} dataForEdit={associationDetail} userProfileOptions={userProfileOptions} roleOptions={roleOptions} userProfileAndRole={userProfileAndRoles} subjectId={associationId.orCrash('association not found')} onRoleSearch={handleRoleSearch} onCancel={handleCloseModal} onSubmit={handleSubmitModal} onUserSearch={handleUserSearch} onSyncValidate={validateDebounced} validate={validate} clearFormValues={clearAssociationDetail} />
                    </Ant.Col>
                </Ant.Row>
            </Modal>}
        </Fragment>;
};

/** Association management table. */
export const ManagementAssociationsTable = memo(ManagementAssociationsTableBase);